import React, { useState } from 'react';
import Draggable from 'react-draggable';
import styled from 'styled-components';
import { Button } from '@mui/material';
import IconBtn from "../IconBtn";
import Tx from '../Tx';
import { INITIAL_STEPS } from './FloorDialog';
import styles from './ImageSelectController.module.css';
import Icn from "../Icn";
import { colors } from '../../maps/colors';

const MIN_ZOOM = 0, MAX_ZOOM = 1, STEP_ZOOM = 0.01;
const MIN_OPACITY = 0, MAX_OPACITY = 1, STEP_OPACITY = 0.1;
const MIN_ROTATE = 0, MAX_ROTATE = 360, STEP_ROTATE = 5;
const MIN_SCALE = 0, MAX_SCALE = 10, STEP_SCALE = 1;
const STEP_ARROWS = 5;

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});


const ImageSelectController = ({ isVisible, onClose, selectedImage, setSelectedImage }) => {
    const [steps, setSteps] = useState(INITIAL_STEPS);
    const { url: imageUrl } = selectedImage;

    const onImageChange = (e) => {
        const imageUrl = e.target.files[0];
        const reader = new FileReader();
        reader.onloadend = function () {
            setSelectedImage(prev => ({ ...prev, url: reader.result }));
        }
        reader.readAsDataURL(imageUrl);
    }

    const onClearImage = () => {
        setSteps(INITIAL_STEPS);
        setSelectedImage(INITIAL_STEPS);
    }

    const handleClick = (key, value) => () => {
        setSelectedImage(prev => {
            const nextValue = prev[key] + +value;
            if (key === 'opacity') {
                if (nextValue < MIN_OPACITY) return ({ ...prev, [key]: MIN_OPACITY });
                else if (nextValue > MAX_OPACITY) return ({ ...prev, [key]: MAX_OPACITY });
            }
            return ({ ...prev, [key]: nextValue });
        });
    }

    const onChange = (key) => (e) => {
        setSteps(prev => ({ ...prev, [key]: e.target.value }))
    }
    if (!isVisible) return null;

    return (
        <Draggable>
            <div className={styles.container}>
                {/* Header */}
                <div className={styles.headerContainer}>
                    <IconBtn
                        onClick={onClose}
                        iconSize={18}
                        style={{ color: '#888', background: 'transparent' }}>
                        closeIcon
                    </IconBtn>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    <div>
                        {!imageUrl && (
                            <Button component="label" variant="contained" startIcon={<Icn style={{ color: colors.WHITE }}>cloudUpload</Icn>}>
                                <Tx>g.uploadImage</Tx>
                                <VisuallyHiddenInput
                                    type="file"
                                    onChange={onImageChange}
                                    accept='image/*'
                                />
                            </Button>
                        )}
                    </div>
                    {imageUrl && (
                        <>
                            {/* Zoom Row */}
                            <div>
                                <IconBtn
                                    onClick={handleClick('zoom', -steps.zoom)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    zoomOut
                                </IconBtn>

                                <IconBtn
                                    onClick={handleClick('zoom', steps.zoom)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    zoomIn
                                </IconBtn>

                                <input
                                    value={steps.zoom}
                                    onChange={onChange('zoom')}
                                    min={MIN_ZOOM}
                                    max={MAX_ZOOM}
                                    step={STEP_ZOOM}
                                    type='number'
                                    className={styles.input}
                                />
                            </div>

                            {/* Rotate Row  */}
                            <div>
                                <IconBtn
                                    onClick={handleClick('rotate', -steps.rotate)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    rotateLeft
                                </IconBtn>

                                <IconBtn
                                    onClick={handleClick('rotate', steps.rotate)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    rotateRight
                                </IconBtn>

                                <input
                                    value={steps.rotate}
                                    onChange={onChange('rotate')}
                                    min={MIN_ROTATE}
                                    max={MAX_ROTATE}
                                    step={STEP_ROTATE}
                                    type='number'
                                    className={styles.input}
                                />
                            </div>

                            {/* Scale Row  */}
                            <div>
                                <IconBtn
                                    onClick={handleClick('scale', -steps.scale)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    textDecreaseIcon
                                </IconBtn>

                                <IconBtn
                                    onClick={handleClick('scale', steps.scale)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    textIncreaseIcon
                                </IconBtn>

                                <input
                                    value={steps.scale}
                                    onChange={onChange('scale')}
                                    min={MIN_SCALE}
                                    max={MAX_SCALE}
                                    step={STEP_SCALE}
                                    type='number'
                                    className={styles.input}
                                />
                            </div>

                            {/* Opacity Row */}
                            <div>
                                <IconBtn
                                    onClick={handleClick('opacity', -steps.opacity)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    flashlightOffIcon
                                </IconBtn>

                                <IconBtn
                                    onClick={handleClick('opacity', steps.opacity)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    flashlightOnIcon
                                </IconBtn>

                                <input
                                    value={steps.opacity}
                                    onChange={onChange('opacity')}
                                    min={MIN_OPACITY}
                                    max={MAX_OPACITY}
                                    step={STEP_OPACITY}
                                    type='number'
                                    className={styles.input}
                                />
                            </div>

                            {/* Arrows */}
                            <div>
                                <IconBtn disabled style={{ opacity: 0 }}>arrowUp</IconBtn>
                                {/* Arrow up */}
                                <IconBtn
                                    onClick={handleClick('vertical', -STEP_ARROWS)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    arrowUp
                                </IconBtn>

                                <div>
                                    {/* Arrow Left */}
                                    <IconBtn
                                        onClick={handleClick('horizontal', -STEP_ARROWS)}
                                        style={{ color: '#888', background: 'transparent' }}>
                                        ptzLeft
                                    </IconBtn>
                                    <IconBtn disabled style={{ opacity: 0 }}>arrowUp</IconBtn>

                                    {/* Arrow Right */}
                                    <IconBtn
                                        onClick={handleClick('horizontal', STEP_ARROWS)}
                                        style={{ color: '#888', background: 'transparent' }}>
                                        ptzRight
                                    </IconBtn>
                                </div>
                                <IconBtn disabled style={{ opacity: 0 }}>arrowDown</IconBtn>

                                {/* Arrow Down  */}
                                <IconBtn
                                    onClick={handleClick('vertical', STEP_ARROWS)}
                                    style={{ color: '#888', background: 'transparent' }}>
                                    arrowDown
                                </IconBtn>
                            </div>
                        </>
                    )}
                    {/* Footer Container */}
                    {imageUrl && (
                        <div className={styles.footerButton} onClick={onClearImage}>
                            <Tx>g.reset</Tx>
                        </div>
                    )}
                </div>
            </div>
        </Draggable>
    )
}

export default ImageSelectController;